import { useEffect } from "react";
import Icon from "../Icon";
import TextField from "../TextField";
import { useAlgoliaSearch } from "./useAlgoliaSearch";
import AlgoliaSearchResults from "./AlgoliaSearchResults";

/**
 * Generic Algolia Quick Search Component - supports any search configuration
 *
 * @param indexName - The name of the Algolia index to search
 * @param onSelect - The function to call when a hit is selected (optional)
 * @param className - The class name of the search component (optional)
 * @param placeholder - The placeholder text of the search component (optional)
 * @param label - The label text of the search component (optional)
 * @param disabled - Whether the search component is disabled (optional)
 * @param defaultValue - The default value of the search component (optional)
 * @param renderHit - The function to call to render the hit (required) as a JSX element
 * @param noResultsMessage - The message to display when no results are found (optional)
 * @param hitsPerPage - The number of hits to display per page (optional)
 * @param displayKey - The key of the hit object to display in the search component (optional)
 * @param initialUiState - Initial search configuration for the Algolia index (optional)
 * @param getDisplayValue - Custom function to determine what value to show in search box after selection (optional)
 * @param onClear - The function to call when the search is cleared (optional)
 */
export interface AlgoliaQuickSearchProps<T = any> {
  indexName: string;
  onSelect?: (hit: T) => void;
  onClear?: () => void;
  onSubmit?: (query: string) => void;
  className?: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  defaultValue?: string;
  renderHit: (hit: T, onSelect: (hit: T) => void, query?: string) => JSX.Element;
  noResultsMessage?: string;
  hitsPerPage?: number;
  displayKey?: string;
  initialUiState?: Record<string, any>;
  getDisplayValue?: (hit: T) => string;
  supportingText?: string;
  error?: boolean;
  errorText?: string;
  isExactMatch?: (hit: T, query: string) => boolean;
  filterHits?: (hits: T[], query: string) => T[];
  required?: boolean;
}

function AlgoliaQuickSearch<T extends Record<string, any>>({
  indexName,
  className,
  placeholder,
  onSelect,
  onClear,
  onSubmit,
  label,
  disabled,
  defaultValue,
  renderHit,
  noResultsMessage,
  hitsPerPage = 7,
  displayKey = "title",
  initialUiState,
  getDisplayValue,
  supportingText,
  error,
  errorText,
  isExactMatch,
  filterHits,
  required
}: AlgoliaQuickSearchProps<T>) {
  const {
    query,
    displayValue,
    results,
    isLoading,
    showResults,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear,
    handleItemSelected,
    setShowResults,
    setIsFocused,
    setDisplayValue,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useAlgoliaSearch<T>({
    indexName,
    hitsPerPage,
    initialUiState,
    displayKey,
    getDisplayValue,
    isExactMatch,
    filterHits,
    onSelect,
    onClear,
    onSubmit,
    defaultValue
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target as Node) &&
        resultsContainerRef.current &&
        !resultsContainerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchBoxRef, resultsContainerRef, setShowResults]);

  useEffect(() => {
    if (!showResults) return;

    const handleGlobalKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setShowResults(false);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [showResults, setShowResults]);

  const trailingIcon =
    displayValue || query ? (
      <button
        className="cursor-pointer flex items-center h-full"
        onClick={handleClear}
        aria-label="Clear search query"
        disabled={disabled}
      >
        <Icon name="cancel" />
      </button>
    ) : undefined;

  return (
    <div className={`relative ${className || ""}`}>
      <div ref={searchBoxRef}>
        <TextField
          label={label}
          placeholder={placeholder}
          value={displayValue || query}
          onChangeText={(value) => {
            setDisplayValue(undefined);
            handleQueryChange(value);
          }}
          onFocus={() => {
            setIsFocused(true);
            if (!disabled && query.trim() !== "") {
              setShowResults(true);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          disabled={disabled}
          leadingIcon="search"
          trailingIcon={trailingIcon}
          variant="outlined"
          className="w-full"
          supportingText={supportingText}
          error={error}
          errorText={errorText}
          required={required}
        />
      </div>

      {showResults && !disabled && query.trim() !== "" && (
        <AlgoliaSearchResults
          results={results}
          query={query}
          isLoading={isLoading}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          handleItemSelected={handleItemSelected}
          itemRefs={itemRefs}
          resultsContainerRef={resultsContainerRef}
          renderHit={renderHit}
          isExactMatch={isExactMatch}
          noResultsMessage={noResultsMessage}
          className="absolute z-50 mt-1 w-full bg-white shadow-md rounded-md p-3 max-h-80 overflow-y-auto"
        />
      )}
    </div>
  );
}

export default AlgoliaQuickSearch;
